import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  BarChart3, Star, Trophy, Puzzle, Dog, ChefHat, Martini, Edit3, Save, X,
  Loader2, Sparkles, TrendingUp, Wand2, ArrowRight, FlaskConical,
} from "lucide-react";
import api, { formatApiError } from "../api";
import {
  Button, Card, Input, Select, Label, Badge, Spinner, EmptyState,
} from "../components/ui/Primitives";
import { hasModule } from "../components/RouteGuards";
import { useAuth } from "../contexts/AuthContext";
import { formatEUR as fmtEur } from "../utils/format";
import MenuPdfCheck from "../components/MenuPdfCheck";


/** Classify a (popularity, profit) point given the chart medians. Mirrors backend logic. */
function classifyPoint(pop, profit, popMed, profitMed) {
  const hiPop = pop >= popMed;
  const hiPr = profit >= profitMed;
  if (hiPop && hiPr) return "star";
  if (hiPop && !hiPr) return "plowhorse";
  if (!hiPop && hiPr) return "puzzle";
  return "dog";
}

const QUADRANT_META = {
  star:      { color: "#22c55e", icon: Star,    label_key: "engineering.q_star" },
  plowhorse: { color: "#3b82f6", icon: Trophy,  label_key: "engineering.q_plowhorse" },
  puzzle:    { color: "#eab308", icon: Puzzle,  label_key: "engineering.q_puzzle" },
  dog:       { color: "#ef4444", icon: Dog,     label_key: "engineering.q_dog" },
};

function Kpi({ label, value, sub, accent = "default", testId }) {
  const cls = accent === "ok" ? "border-ok/30" : accent === "warn" ? "border-warn/30" : accent === "brand" ? "border-brand/30" : "border-white/10";
  return (
    <div className={`bg-bg-surface border ${cls} rounded-xl px-4 py-3`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-ink-muted font-medium">{label}</div>
      <div className="num text-xl font-medium text-ink-primary tracking-tight mt-1">{value}</div>
      {sub && <div className="text-[10px] text-ink-muted mt-0.5">{sub}</div>}
    </div>
  );
}

/** Scatter plot: X = popularity (sales_count), Y = profit_per_unit.
 *  Renders a crosshair at the median thresholds — quadrants colored.
 *  `simulation` is an optional {dish_id, projected_pop, projected_profit} overlay
 *   that draws an arrow from the dish's actual point to its projected point. */
function ScatterPlot({ dishes, thresholds, onSelect, simulation }) {
  const { t } = useTranslation();
  const W = 800, H = 440;
  const PAD_L = 64, PAD_R = 24, PAD_T = 36, PAD_B = 52;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const xs = dishes.map((d) => d.sales_count);
  const ys = dishes.map((d) => d.profit_per_unit);
  const popMed = Number(thresholds?.popularity_median || 0);
  const profMed = Number(thresholds?.profitability_median || 0);

  // Always show a reasonable plotting domain: real range + ≥10% headroom, never <1
  const xRawMax = Math.max(...xs, popMed * 2, 1);
  const xMax = xRawMax * 1.1;
  const yRawMin = Math.min(...ys, 0);
  const yRawMax = Math.max(...ys, profMed * 2, 1);
  const yPad = (yRawMax - yRawMin) * 0.1 || 1;
  const yMin = yRawMin - yPad;
  const yMax = yRawMax + yPad;

  const sx = (x) => PAD_L + (Math.max(0, x) / xMax) * innerW;
  const sy = (y) => PAD_T + innerH - ((y - yMin) / (yMax - yMin)) * innerH;
  const cx = sx(popMed);
  const cy = sy(profMed);

  // Use SVG `width=100%` + viewBox for responsive scaling
  return (
    <div className="bg-bg-surface border border-white/10 rounded-xl p-3" data-testid="engineering-scatter">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-auto block"
        style={{ maxHeight: 480 }}
      >
        {/* Quadrant backgrounds — top-left=Puzzle, top-right=Star, bottom-left=Dog, bottom-right=Plowhorse */}
        <rect x={PAD_L} y={PAD_T} width={cx - PAD_L} height={cy - PAD_T} fill="#eab308" opacity="0.07" />
        <rect x={cx} y={PAD_T} width={PAD_L + innerW - cx} height={cy - PAD_T} fill="#22c55e" opacity="0.07" />
        <rect x={PAD_L} y={cy} width={cx - PAD_L} height={PAD_T + innerH - cy} fill="#ef4444" opacity="0.07" />
        <rect x={cx} y={cy} width={PAD_L + innerW - cx} height={PAD_T + innerH - cy} fill="#3b82f6" opacity="0.07" />

        {/* Crosshair (median lines) — clearly visible */}
        <line x1={cx} y1={PAD_T} x2={cx} y2={PAD_T + innerH} stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" strokeDasharray="5 4" />
        <line x1={PAD_L} y1={cy} x2={PAD_L + innerW} y2={cy} stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" strokeDasharray="5 4" />

        {/* Axes */}
        <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + innerH} stroke="rgba(255,255,255,0.25)" />
        <line x1={PAD_L} y1={PAD_T + innerH} x2={PAD_L + innerW} y2={PAD_T + innerH} stroke="rgba(255,255,255,0.25)" />

        {/* Quadrant corner labels — anchored to corners with safe padding */}
        <text x={PAD_L + innerW - 10} y={PAD_T + 18} fontSize="13" fontWeight="700" fill="#22c55e" textAnchor="end">★ {t("engineering.q_star")}</text>
        <text x={PAD_L + innerW - 10} y={PAD_T + innerH - 10} fontSize="13" fontWeight="700" fill="#3b82f6" textAnchor="end">{t("engineering.q_plowhorse")}</text>
        <text x={PAD_L + 10} y={PAD_T + 18} fontSize="13" fontWeight="700" fill="#eab308" textAnchor="start">{t("engineering.q_puzzle")}</text>
        <text x={PAD_L + 10} y={PAD_T + innerH - 10} fontSize="13" fontWeight="700" fill="#ef4444" textAnchor="start">{t("engineering.q_dog")}</text>

        {/* Tick labels — X */}
        <text x={PAD_L} y={PAD_T + innerH + 16} fontSize="10" fill="#7d8590" textAnchor="middle" fontFamily="ui-monospace">0</text>
        <text x={PAD_L + innerW} y={PAD_T + innerH + 16} fontSize="10" fill="#7d8590" textAnchor="middle" fontFamily="ui-monospace">{Math.round(xRawMax)}</text>
        {popMed > 0 && (
          <text x={cx} y={PAD_T + innerH + 16} fontSize="10" fill="#7dd3fc" textAnchor="middle" fontFamily="ui-monospace" fontWeight="600">Ø {popMed.toFixed(0)}</text>
        )}
        {/* Tick labels — Y */}
        <text x={PAD_L - 8} y={PAD_T + 5} fontSize="10" fill="#7d8590" textAnchor="end" fontFamily="ui-monospace">{fmtEur(yMax)}</text>
        <text x={PAD_L - 8} y={PAD_T + innerH + 4} fontSize="10" fill="#7d8590" textAnchor="end" fontFamily="ui-monospace">{fmtEur(Math.max(0, yMin))}</text>
        <text x={PAD_L - 8} y={cy + 4} fontSize="10" fill="#7dd3fc" textAnchor="end" fontFamily="ui-monospace" fontWeight="600">Ø {fmtEur(profMed)}</text>

        {/* Axis titles */}
        <text x={PAD_L + innerW / 2} y={H - 14} fontSize="11" fill="#7d8590" textAnchor="middle">{t("engineering.x_axis")}</text>
        <text x={18} y={PAD_T + innerH / 2} fontSize="11" fill="#7d8590" textAnchor="middle" transform={`rotate(-90, 18, ${PAD_T + innerH / 2})`}>{t("engineering.y_axis")}</text>

        {/* Simulation arrow — drawn before dots so dots overlay it at endpoints */}
        {simulation && (() => {
          const src = dishes.find((d) => d.id === simulation.dish_id);
          if (!src) return null;
          const x1 = sx(src.sales_count);
          const y1 = sy(src.profit_per_unit);
          const x2 = sx(simulation.projected_pop);
          const y2 = sy(simulation.projected_profit);
          if (Math.abs(x1 - x2) < 1 && Math.abs(y1 - y2) < 1) return null;
          return (
            <g data-testid="scatter-simulation">
              <defs>
                <marker id="simArrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#a78bfa" />
                </marker>
              </defs>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#a78bfa" strokeWidth="2.5" strokeDasharray="6 4" markerEnd="url(#simArrow)" opacity="0.95" />
              <circle cx={x2} cy={y2} r="9" fill="none" stroke="#a78bfa" strokeWidth="2" opacity="0.7" />
            </g>
          );
        })()}

        {/* Dots — drawn last so they sit on top of all overlays */}
        {dishes.map((d) => {
          const color = QUADRANT_META[d.quadrant]?.color || "#7d8590";
          const x = sx(d.sales_count);
          const y = sy(d.profit_per_unit);
          return (
            <g key={`${d.kind}-${d.id}`} onClick={() => onSelect(d)} style={{ cursor: "pointer" }} data-testid={`scatter-dot-${d.id}`}>
              <circle cx={x} cy={y} r="7" fill={color} stroke="#0d1117" strokeWidth="2" opacity="0.95" />
              <circle cx={x} cy={y} r="3" fill="#ffffff" opacity="0.9" />
              <title>{d.name} · {d.sales_count}× · {fmtEur(d.profit_per_unit)}</title>
            </g>
          );
        })}
      </svg>
      {/* Legend below */}
      <div className="flex flex-wrap justify-center gap-3 mt-2 text-[10px] text-ink-muted">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: "#22c55e" }} /> {t("engineering.q_star")}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: "#3b82f6" }} /> {t("engineering.q_plowhorse")}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: "#eab308" }} /> {t("engineering.q_puzzle")}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: "#ef4444" }} /> {t("engineering.q_dog")}</span>
      </div>
    </div>
  );
}

function SalesEditor({ dish, onSaved, onClose }) {
  const { t } = useTranslation();
  const [val, setVal] = useState(String(dish.sales_count || 0));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const n = parseInt(val, 10);
    if (!Number.isFinite(n) || n < 0) { toast.error(t("engineering.sales_invalid")); return; }
    setSaving(true);
    try {
      await api.patch(`/engineering/sales/${dish.kind}/${dish.id}`, { sales_count: n });
      toast.success(t("engineering.saved"));
      onSaved(n);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg-void/80 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-bg-surface border border-white/10 rounded-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="sales-editor">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">{dish.kind === "recipe" ? t("engineering.recipe") : t("engineering.cocktail")}</div>
            <div className="text-base font-semibold text-ink-primary">{dish.name}</div>
            <div className="text-xs text-ink-secondary">{dish.category}</div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-primary"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-bg-void rounded-md px-3 py-2">
              <div className="text-ink-muted text-[10px] uppercase tracking-wider">{t("engineering.profit_per_unit")}</div>
              <div className="num text-ink-primary font-medium text-sm">{fmtEur(dish.profit_per_unit)}</div>
            </div>
            <div className="bg-bg-void rounded-md px-3 py-2">
              <div className="text-ink-muted text-[10px] uppercase tracking-wider">{t("engineering.fc_pct")}</div>
              <div className="num text-ink-primary font-medium text-sm">{Number(dish.food_cost_pct).toFixed(1)} %</div>
            </div>
          </div>
          <div>
            <Label>{t("engineering.sales_count")}</Label>
            <Input type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} data-testid="sales-input" autoFocus />
            <p className="text-[11px] text-ink-muted mt-1">{t("engineering.sales_hint")}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="sales-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {t("common.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** What-if price simulation panel. Lets the user move the selling price ±50%
 *  and shows real-time recompute of FC%, DB per unit, projected quadrant. */
function WhatIfPanel({ dish, thresholds, onClose, onSimulationChange }) {
  const { t } = useTranslation();
  const basePrice = Number(dish?.price_per_unit || 0);
  const baseCost = Number(dish?.cost_per_unit || 0);
  const [price, setPrice] = useState(basePrice);

  // Reset slider when target dish changes
  useEffect(() => { setPrice(Number(dish?.price_per_unit || 0)); }, [dish]);

  const sim = useMemo(() => {
    const profit = price - baseCost;
    const fcPct = price > 0 ? (baseCost / price) * 100 : 0;
    const quadrant = classifyPoint(
      dish.sales_count, profit,
      Number(thresholds?.popularity_median || 0),
      Number(thresholds?.profitability_median || 0),
    );
    const totalProfit = profit * dish.sales_count;
    return { profit, fcPct, quadrant, totalProfit };
  }, [price, baseCost, dish, thresholds]);

  // Push the projected position upward so the scatter shows the arrow
  useEffect(() => {
    onSimulationChange({
      dish_id: dish.id,
      projected_pop: dish.sales_count,
      projected_profit: sim.profit,
    });
    return () => onSimulationChange(null);
  }, [dish.id, dish.sales_count, sim.profit, onSimulationChange]);

  if (!dish) return null;
  const min = basePrice * 0.5;
  const max = basePrice * 1.5;
  const step = Math.max(0.01, basePrice * 0.01);
  const deltaPct = basePrice > 0 ? ((price - basePrice) / basePrice) * 100 : 0;
  const fromMeta = QUADRANT_META[dish.quadrant] || QUADRANT_META.dog;
  const toMeta = QUADRANT_META[sim.quadrant] || QUADRANT_META.dog;
  const movedQuadrant = sim.quadrant !== dish.quadrant;

  return (
    <div className="fixed inset-0 z-40 flex justify-end pointer-events-none">
      <div className="bg-bg-surface border-l border-white/10 w-full max-w-md h-full overflow-y-auto pointer-events-auto shadow-2xl" data-testid="whatif-panel">
        <div className="sticky top-0 bg-bg-surface border-b border-white/10 px-5 py-4 flex items-center justify-between z-10">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-wider text-brand font-medium flex items-center gap-1.5">
              <FlaskConical size={11} /> {t("engineering.whatif_kicker")}
            </div>
            <div className="text-base font-semibold text-ink-primary truncate">{dish.name}</div>
            <div className="text-[11px] text-ink-muted">{dish.category}</div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-primary p-1 ml-2" data-testid="whatif-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Quadrant transition */}
          <div className="bg-bg-void rounded-lg p-4 border border-white/5" data-testid="whatif-quadrants">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2 font-medium">{t("engineering.whatif_position")}</div>
            <div className="flex items-center gap-3">
              <Badge tone="default" style={{ color: fromMeta.color, borderColor: fromMeta.color + "40" }}>
                {t(fromMeta.label_key)}
              </Badge>
              <ArrowRight size={14} className={movedQuadrant ? "text-brand" : "text-ink-muted"} />
              <Badge tone="default" style={{ color: toMeta.color, borderColor: toMeta.color + "40", borderWidth: 2 }} data-testid="whatif-projected-quadrant">
                {t(toMeta.label_key)}
              </Badge>
              {movedQuadrant && <span className="text-[10px] text-brand font-medium">{t("engineering.whatif_moved")}</span>}
            </div>
          </div>

          {/* Price slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="m-0">{t("engineering.whatif_price")}</Label>
              <div className="num text-sm font-medium text-ink-primary" data-testid="whatif-price-value">
                {fmtEur(price)}
                <span className={`ml-2 text-[11px] ${deltaPct > 0 ? "text-ok" : deltaPct < 0 ? "text-err" : "text-ink-muted"}`}>
                  {deltaPct >= 0 ? "+" : ""}{deltaPct.toFixed(1)}%
                </span>
              </div>
            </div>
            <input
              type="range"
              min={min} max={max} step={step}
              value={price}
              onChange={(e) => setPrice(Number(e.target.value))}
              className="w-full accent-brand"
              data-testid="whatif-slider"
            />
            <div className="flex justify-between text-[10px] text-ink-muted mt-1 num">
              <span>−50% · {fmtEur(min)}</span>
              <span>{t("engineering.whatif_now")}: {fmtEur(basePrice)}</span>
              <span>+50% · {fmtEur(max)}</span>
            </div>
          </div>

          {/* Live KPIs */}
          <div className="grid grid-cols-2 gap-2">
            <Kpi label={t("engineering.fc_pct")} value={`${sim.fcPct.toFixed(1)} %`} accent={sim.fcPct > 30 ? "warn" : "ok"} testId="whatif-fc" />
            <Kpi label={t("engineering.profit_per_unit")} value={fmtEur(sim.profit)} accent={sim.profit > dish.profit_per_unit ? "ok" : sim.profit < dish.profit_per_unit ? "warn" : "default"} testId="whatif-cm" />
            <Kpi label={t("engineering.col_total_profit")} value={fmtEur(sim.totalProfit)} sub={`${dish.sales_count} × ${fmtEur(sim.profit)}`} testId="whatif-total" />
            <Kpi label={t("engineering.col_sales")} value={dish.sales_count} sub={t("engineering.whatif_sales_fixed")} />
          </div>

          {/* Action: save the new price to the recipe/cocktail */}
          <ApplyPriceButton dish={dish} price={price} basePrice={basePrice} onApplied={onClose} />
        </div>
      </div>
    </div>
  );
}

function ApplyPriceButton({ dish, price, basePrice, onApplied }) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const changed = Math.abs(price - basePrice) > 0.001;
  const apply = async () => {
    setSaving(true);
    try {
      const url = dish.kind === "cocktail" ? `/cocktails/${dish.id}` : `/recipes/${dish.id}`;
      await api.patch(url, { selling_price: Number(price.toFixed(2)) });
      toast.success(t("engineering.whatif_applied"));
      onApplied();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Button onClick={apply} disabled={!changed || saving} className="w-full" data-testid="whatif-apply">
      {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
      {t("engineering.whatif_apply", { price: `${price.toFixed(2).replace(".", ",")} €` })}
    </Button>
  );
}


export default function EngineeringPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("all");
  const [quadrant, setQuadrant] = useState("");
  const [editor, setEditor] = useState(null);
  const [whatIf, setWhatIf] = useState(null);
  const [simOverlay, setSimOverlay] = useState(null);
  const canAI = hasModule(user, "ai");

  const handleReinvent = (dish) => {
    if (!canAI) { toast.error(t("waste.ai_no_plan")); return; }
    navigate("/ai", {
      state: {
        intent: "reinvent_dog",
        kind: dish.kind,
        dish_name: dish.name,
        category: dish.category,
      },
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/engineering/matrix?scope=${scope}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!quadrant) return data.dishes;
    return data.dishes.filter((d) => d.quadrant === quadrant);
  }, [data, quadrant]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => b.total_profit - a.total_profit);
  }, [filtered]);

  return (
    <div className="space-y-6 fade-in" data-testid="engineering-page">
      <header>
        <div className="text-[11px] uppercase tracking-wider text-brand mb-1 flex items-center gap-2">
          <BarChart3 size={12} /> {t("engineering.kicker")}
        </div>
        <h1 className="text-2xl font-semibold text-ink-primary">{t("engineering.title")}</h1>
        <p className="text-sm text-ink-secondary mt-1 max-w-2xl">{t("engineering.subtitle")}</p>
      </header>

      {/* Phase 30 F8 — PDF Live-Check */}
      <MenuPdfCheck
        onItemAdded={(kind, name, price) => {
          const path = kind === "kitchen" ? "/recipes" : "/cocktails";
          navigate(path, { state: { prefill_name: name, prefill_price: price } });
        }}
        onBulkAdded={(kind, count) => {
          const path = kind === "kitchen" ? "/recipes" : "/cocktails";
          // Brief delay so the success toast is readable before navigation
          setTimeout(() => navigate(path, { state: { filter_status: "draft", bulk_added: count } }), 800);
        }}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label>{t("engineering.scope")}</Label>
          <Select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="eng-scope">
            <option value="all">{t("engineering.scope_all")}</option>
            <option value="recipes">{t("engineering.scope_recipes")}</option>
            <option value="cocktails">{t("engineering.scope_cocktails")}</option>
          </Select>
        </div>
        <div>
          <Label>{t("engineering.quadrant_filter")}</Label>
          <Select value={quadrant} onChange={(e) => setQuadrant(e.target.value)} data-testid="eng-quadrant">
            <option value="">{t("common.all")}</option>
            <option value="star">★ {t("engineering.q_star")}</option>
            <option value="plowhorse">{t("engineering.q_plowhorse")}</option>
            <option value="puzzle">{t("engineering.q_puzzle")}</option>
            <option value="dog">{t("engineering.q_dog")}</option>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Spinner size={24} /></div>
      ) : !data || data.dishes.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title={t("engineering.empty_title")}
          description={t("engineering.empty_sub")}
        />
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label={t("engineering.kpi_dishes")} value={data.totals.dishes} testId="eng-kpi-dishes" />
            <Kpi label={t("engineering.kpi_units")} value={data.totals.units_sold} testId="eng-kpi-units" />
            <Kpi label={t("engineering.kpi_revenue")} value={fmtEur(data.totals.total_revenue)} testId="eng-kpi-revenue" />
            <Kpi label={t("engineering.kpi_profit")} value={fmtEur(data.totals.total_profit)} accent="ok" testId="eng-kpi-profit" />
            <Kpi label={t("engineering.kpi_avg")} value={fmtEur(data.totals.avg_profit_per_dish)} sub={t("engineering.per_dish")} testId="eng-kpi-avg" />
          </div>

          {/* Quadrant counts */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {["star", "plowhorse", "puzzle", "dog"].map((q) => {
              const meta = QUADRANT_META[q];
              const Icon = meta.icon;
              return (
                <button
                  key={q}
                  onClick={() => setQuadrant(quadrant === q ? "" : q)}
                  className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border transition-all text-left ${quadrant === q ? "bg-brand/10 border-brand/40" : "bg-bg-surface border-white/10 hover:border-white/20"}`}
                  data-testid={`eng-quadrant-${q}`}
                >
                  <div>
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-ink-muted font-medium">
                      <Icon size={11} style={{ color: meta.color }} />
                      {t(meta.label_key)}
                    </div>
                    <div className="num text-lg font-medium text-ink-primary mt-0.5">{data.summary[q]}</div>
                  </div>
                  <div className="text-[10px] text-ink-muted leading-tight max-w-[80px] text-right">{t(`engineering.tip_${q}`)}</div>
                </button>
              );
            })}
          </div>

          {/* Scatter */}
          <ScatterPlot dishes={data.dishes} thresholds={data.thresholds} onSelect={setWhatIf} simulation={simOverlay} />

          {/* Dishes table */}
          <Card className="overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5 text-xs uppercase tracking-wider text-ink-muted font-medium flex items-center gap-2">
              <TrendingUp size={12} /> {t("engineering.table_title")}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="engineering-table">
                <thead className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">{t("engineering.col_quadrant")}</th>
                    <th className="text-left px-4 py-2.5 font-medium">{t("engineering.col_dish")}</th>
                    <th className="text-right px-4 py-2.5 font-medium">{t("engineering.col_sales")}</th>
                    <th className="text-right px-4 py-2.5 font-medium">{t("engineering.col_profit_unit")}</th>
                    <th className="text-right px-4 py-2.5 font-medium">{t("engineering.col_total_profit")}</th>
                    <th className="text-right px-4 py-2.5 font-medium">{t("engineering.col_fc")}</th>
                    <th className="text-right px-4 py-2.5 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((d) => {
                    const meta = QUADRANT_META[d.quadrant];
                    return (
                      <tr key={`${d.kind}-${d.id}`} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`eng-row-${d.id}`}>
                        <td className="px-4 py-3">
                          <Badge tone="default" style={{ color: meta.color, borderColor: meta.color + "40" }}>
                            {t(meta.label_key)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {d.kind === "recipe" ? <ChefHat size={13} className="text-brand" /> : <Martini size={13} className="text-warn" />}
                            <div>
                              <div className="text-ink-primary font-medium">{d.name}</div>
                              <div className="text-[11px] text-ink-muted">{d.category}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 num text-right text-ink-primary">{d.sales_count}</td>
                        <td className="px-4 py-3 num text-right text-ink-secondary">{fmtEur(d.profit_per_unit)}</td>
                        <td className="px-4 py-3 num text-right text-ok font-medium">{fmtEur(d.total_profit)}</td>
                        <td className="px-4 py-3 num text-right text-ink-secondary">{Number(d.food_cost_pct).toFixed(1)} %</td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            {d.quadrant === "dog" && canAI && (
                              <Button variant="ghost" size="sm" onClick={() => handleReinvent(d)} data-testid={`eng-reinvent-${d.id}`}>
                                <Wand2 size={12} className="text-warn" /> {t("engineering.reinvent")}
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => setWhatIf(d)} data-testid={`eng-whatif-${d.id}`}>
                              <FlaskConical size={12} /> {t("engineering.whatif")}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditor(d)} data-testid={`eng-edit-${d.id}`}>
                              <Edit3 size={12} /> {t("engineering.set_sales")}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {editor && (
        <SalesEditor
          dish={editor}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }}
        />
      )}
      {whatIf && (
        <WhatIfPanel
          dish={whatIf}
          thresholds={data?.thresholds}
          onClose={() => { setWhatIf(null); setSimOverlay(null); load(); }}
          onSimulationChange={setSimOverlay}
        />
      )}
    </div>
  );
}
